import axios from 'axios';
import i18next from '@/core/i18n';
import { env } from '@/config/env';
import { fromAxiosError } from './types';
import { AUTH_UNAUTHORIZED_EVENT, TOKEN_STORAGE_KEY } from '@/core/auth/types';
// Type-only imports upstream, so no cycle back into this module.
import { decodeJwt, isExpired } from '@/core/auth/jwt';

/**
 * Singleton axios instance for all .NET API calls.
 *
 * Rules:
 *  - Only this file imports axios directly. Endpoint modules import `apiClient`.
 *  - The token is read directly from localStorage (not from React context) so
 *    axios calls work correctly outside the React tree (query functions, event
 *    handlers, etc.). This is the conventional pattern for axios + React auth.
 *  - 401 responses dispatch a custom window event so AuthProvider can clear
 *    state without a circular import between client.ts and AuthContext.
 */
export const apiClient = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: 30_000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ---------------------------------------------------------------------------
// Request interceptor — attach Bearer token from localStorage
// ---------------------------------------------------------------------------
apiClient.interceptors.request.use((config) => {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * True when the stored token is missing, unparseable, or expired — i.e. a 401
 * really does mean the session is over rather than "not allowed here".
 *
 * Deliberately conservative: anything we can't read is treated as dead, so a
 * corrupted token still logs the user out instead of trapping them in a UI
 * that 401s on every request.
 */
function isSessionDead(): boolean {
  const token = localStorage.getItem(TOKEN_STORAGE_KEY);
  if (!token) return true;
  const claims = decodeJwt(token);
  if (!claims) return true;
  return isExpired(claims);
}

// ---------------------------------------------------------------------------
// Response interceptor — normalise errors + signal 401 to AuthProvider
// ---------------------------------------------------------------------------
apiClient.interceptors.response.use(
  // Pass-through on success — callers unwrap `.data` themselves or the endpoint
  // helper does it for them.
  (response) => response,
  (error: unknown) => {
    const apiError = fromAxiosError(error);

    // Dispatch a custom event on 401 so AuthProvider can clear its state.
    // Using window events (instead of importing AuthContext) keeps this module
    // free of React dependencies and avoids circular imports.
    //
    // But ONLY when the session is actually dead. The API returns 401 for two
    // very different things: "your token is no good" AND "you may not touch this
    // resource" (JWTConfigurator.UserPermission throws Unauthorized rather than
    // Forbidden when a route is missing from the caller's menu claim). Treating
    // both as a dead session meant one permission-gated endpoint would sign the
    // user out mid-navigation — land on the dashboard, click any restricted
    // page, get bounced to /login with a perfectly valid token still in hand.
    //
    // So: log out only if there is no token, or it no longer decodes//has
    // expired. Otherwise let the error through and let the page show its own
    // error state — the user stays signed in.
    if (apiError.status === 401 && isSessionDead()) {
      window.dispatchEvent(new CustomEvent(AUTH_UNAUTHORIZED_EVENT));
    }

    // A 401 against a live session is the permission case described above, and
    // the API's own message for it is the bare word "Unauthorized" — accurate
    // to HTTP, useless to the person reading the page, and easily mistaken for
    // "you were signed out". Swap in the phrasing RequireAuth already uses for
    // the same situation so the two agree.
    if (apiError.status === 401 && !isSessionDead()) {
      apiError.code = 'PERMISSION_DENIED';
      apiError.message = i18next.t('errors.forbidden');
    }

    // Normalise into ApiError and re-throw so callers get a typed error.
    // We never swallow — the caller (useQuery/useMutation) handles display.
    return Promise.reject(apiError);
  }
);
